import React, { useState } from 'react';
import { Search, Users, Star, MapPin, Briefcase, Crown, Shield, CheckCircle, MessageSquare, Calendar, Music } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ContactDirectory = ({ 
  contacts, 
  currentUser, 
  searchQuery, 
  onSearchChange,
  onInitiateConnection,
  onStartBooking,
  anonymousMode,
  userSubscription,
  subscriptionPlans
}) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [showFilters, setShowFilters] = useState(false);

  const filterOptions = [
    { id: 'all', label: 'Tous les professionnels', count: contacts?.length || 0 },
    { id: 'artists', label: 'Artistes', count: contacts?.filter(c => c?.partners?.[0]?.partner_type === 'artist')?.length || 0 },
    { id: 'venues', label: 'Lieux & Salles', count: contacts?.filter(c => c?.partners?.[0]?.partner_type === 'venue')?.length || 0 },
    { id: 'agents', label: 'Agents & Managers', count: contacts?.filter(c => c?.partners?.[0]?.partner_type === 'agent')?.length || 0 },
    { id: 'bookers', label: 'Bookers', count: contacts?.filter(c => c?.partners?.[0]?.partner_type === 'booker')?.length || 0 },
    { id: 'verified', label: 'Profils Vérifiés', count: contacts?.filter(c => c?.partners?.[0]?.is_verified)?.length || 0 }
  ];

  const sortOptions = [
    { id: 'rating', label: 'Note' },
    { id: 'activity', label: 'Activité récente' },
    { id: 'subscription', label: 'Type d\'abonnement' },
    { id: 'location', label: 'Localisation' }
  ];

  const filteredContacts = contacts?.filter(contact => {
    const matchesSearch = contact?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         contact?.partners?.[0]?.business_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         contact?.industry_specialization?.some(spec => 
                           spec?.toLowerCase()?.includes(searchQuery?.toLowerCase())
                         );
    
    const matchesFilter = selectedFilter === 'all' || 
                         contact?.partners?.[0]?.partner_type === selectedFilter ||
                         (selectedFilter === 'verified' && contact?.partners?.[0]?.is_verified);

    return matchesSearch && matchesFilter;
  })?.sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return (b?.reputation_score || 0) - (a?.reputation_score || 0);
      case 'activity':
        return new Date(b?.last_activity) - new Date(a?.last_activity);
      case 'subscription':
        const subscriptionOrder = { 'business': 4, 'pro': 3, 'venue': 2, 'free': 1 };
        return (subscriptionOrder?.[b?.subscription_tier] || 0) - (subscriptionOrder?.[a?.subscription_tier] || 0);
      default:
        return 0;
    }
  }) || [];

  const getPartnerIcon = (partnerType) => {
    switch (partnerType) {
      case 'artist': return <Music className="w-5 h-5 text-purple-600" />;
      case 'venue': return <MapPin className="w-5 h-5 text-green-600" />;
      case 'agent': return <Briefcase className="w-5 h-5 text-blue-600" />;
      case 'booker': return <Calendar className="w-5 h-5 text-orange-600" />;
      default: return <Users className="w-5 h-5 text-gray-600" />;
    }
  };

  const getSubscriptionBadge = (subscriptionType) => {
    const badges = {
      'free': { color: 'bg-gray-100 text-gray-800', label: 'Gratuit' },
      'pro': { color: 'bg-blue-100 text-blue-800', label: 'Pro' },
      'business': { color: 'bg-purple-100 text-purple-800', label: 'Business' },
      'venue': { color: 'bg-green-100 text-green-800', label: 'Venue' }
    };

    const badge = badges?.[subscriptionType] || badges?.free;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge?.color}`}>
        {badge?.label}
      </span>
    );
  };

  const canContact = (contact) => {
    const userPlan = subscriptionPlans?.[userSubscription];
    const contactsUsed = contacts?.length || 0;
    return userPlan?.contactLimit === -1 || contactsUsed < userPlan?.contactLimit;
  };

  const formatLastActivity = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Aujourd\'hui';
    if (days === 1) return 'Hier';
    if (days < 7) return `Il y a ${days} jours`;
    return `Il y a ${Math.floor(days / 7)} semaines`;
  };

  return (
    <div className="flex h-full">
      {/* Filters Sidebar */}
      <div className="w-80 border-r border-gray-200 bg-gray-50">
        <div className="p-6">
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher des professionnels..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Filtres</h3>
            <div className="space-y-2">
              {filterOptions?.map((option) => (
                <button
                  key={option?.id}
                  onClick={() => setSelectedFilter(option?.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    selectedFilter === option?.id
                      ? 'bg-blue-100 text-blue-800 font-medium' :'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{option?.label}</span>
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                      {option?.count}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Trier par</h3>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {sortOptions?.map((option) => (
                <option key={option?.id} value={option?.id}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>

          {/* Premium Features Notice */}
          {userSubscription === 'free' && (
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Crown className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-purple-900 mb-1">
                    Accès Limité
                  </h4>
                  <p className="text-xs text-purple-700 mb-2">
                    Contactez jusqu'à {subscriptionPlans?.free?.contactLimit} professionnels avec votre plan gratuit.
                  </p>
                  <Button variant="outline" size="sm" className="text-xs">
                    Passer Premium
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contacts Grid */}
      <div className="flex-1 p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Annuaire Professionnel Sécurisé
              </h2>
              <p className="text-sm text-gray-600">
                {filteredContacts?.length} professionnels vérifiés • Communication anonyme
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-green-50 px-3 py-1 rounded-full">
                <Shield className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600 font-medium">
                  Coordonnées Protégées
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredContacts?.map((contact) => (
            <div key={contact?.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <img
                      src={contact?.avatar_url || '/api/placeholder/48/48'}
                      alt=""
                      className="w-12 h-12 rounded-full"
                    />
                    {contact?.partners?.[0]?.is_verified && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {anonymousMode ? contact?.display_name : contact?.full_name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {contact?.partners?.[0]?.business_name || 'Professionnel indépendant'}
                    </p>
                  </div>
                </div>
                {getPartnerIcon(contact?.partners?.[0]?.partner_type)}
              </div>

              <div className="mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium">{contact?.reputation_score?.toFixed(1)}</span>
                  <span className="text-xs text-gray-500">
                    • {formatLastActivity(contact?.last_activity)}
                  </span>
                </div>
                
                {contact?.partners?.[0]?.location && (
                  <div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>{contact?.partners?.[0]?.location}</span>
                  </div>
                )}

                <div className="flex items-center space-x-2 mb-3">
                  {getSubscriptionBadge(contact?.subscription_tier)}
                  {contact?.partners?.[0]?.is_verified && (
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                      Vérifié
                    </span>
                  )}
                </div>
              </div>

              {/* Specializations */}
              {contact?.industry_specialization?.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {contact?.industry_specialization?.slice(0, 3)?.map((spec, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                      >
                        {spec}
                      </span>
                    ))}
                    {contact?.industry_specialization?.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                        +{contact?.industry_specialization?.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2">
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => onInitiateConnection(contact)}
                    disabled={!canContact(contact)}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Contacter
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1"
                    onClick={() => onStartBooking(contact)}
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Booking
                  </Button>
                </div>
                
                {!canContact(contact) && (
                  <p className="text-xs text-amber-600 text-center">
                    Limite de contacts atteinte • Passez Premium
                  </p>
                )}
              </div>

              {/* Privacy Protection Notice */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center space-x-2">
                  <Shield className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    Coordonnées révélées après accord mutuel
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredContacts?.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucun professionnel trouvé
            </h3>
            <p className="text-gray-600">
              Essayez de modifier vos filtres ou votre recherche
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactDirectory;